
import { useState } from 'react';

interface PriceAlertFormProps {
  productId: number;
  productName: string;
  currentPrice: number;
  lowestPrice: number;
}

const PriceAlertForm = ({ productId, productName, currentPrice, lowestPrice }: PriceAlertFormProps) => {
  const [email, setEmail] = useState('');
  const [targetPrice, setTargetPrice] = useState(Math.round(currentPrice * 0.9));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [alertType, setAlertType] = useState<'target' | 'any'>('target');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);

    const formData = new URLSearchParams();
    formData.append('email', email);
    formData.append('productId', productId.toString());
    formData.append('productName', productName);
    formData.append('currentPrice', currentPrice.toString());
    formData.append('targetPrice', alertType === 'target' ? targetPrice.toString() : 'any');
    formData.append('alertType', alertType);

    try {
      await fetch('https://readdy.ai/api/form/d69fth5t1k61917cm7gg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Alert Created!</h3>
          <p className="text-gray-600 text-sm">
            We will notify you at <strong>{email}</strong> when the price drops
            {alertType === 'target' ? ` to $${targetPrice} or below` : ''}.
          </p>
          <button
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium cursor-pointer"
          >
            Create another alert
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
          <i className="ri-notification-3-line text-xl text-teal-600"></i>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Price Drop Alert</h3>
          <p className="text-sm text-gray-500">Get notified when the price drops</p>
        </div>
      </div>

      <form id="price-alert-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
        {/* Alert Type Selection */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setAlertType('target')}
            className={`p-3 rounded-lg border text-left transition-all cursor-pointer ${
              alertType === 'target'
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <i className={`ri-focus-3-line ${alertType === 'target' ? 'text-teal-600' : 'text-gray-400'}`}></i>
              <span className={`text-sm font-medium ${alertType === 'target' ? 'text-teal-700' : 'text-gray-700'}`}>
                Target Price
              </span>
            </div>
            <p className="text-xs text-gray-500">Alert when price reaches your target</p>
          </button>
          <button
            type="button"
            onClick={() => setAlertType('any')}
            className={`p-3 rounded-lg border text-left transition-all cursor-pointer ${
              alertType === 'any'
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <i className={`ri-arrow-down-line ${alertType === 'any' ? 'text-teal-600' : 'text-gray-400'}`}></i>
              <span className={`text-sm font-medium ${alertType === 'any' ? 'text-teal-700' : 'text-gray-700'}`}>
                Any Drop
              </span>
            </div>
            <p className="text-xs text-gray-500">Alert on any price decrease</p>
          </button>
        </div>

        {/* Target Price Input */}
        {alertType === 'target' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Price
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                name="targetPrice"
                value={targetPrice}
                onChange={(e) => setTargetPrice(Number(e.target.value))}
                min={1}
                max={currentPrice - 1}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>Historical low: ${lowestPrice}</span>
              <span>Current: ${currentPrice}</span>
            </div>
            {/* Quick select buttons */}
            <div className="flex gap-2 mt-3">
              {[10, 15, 20, 25].map((percent) => (
                <button
                  key={percent}
                  type="button"
                  onClick={() => setTargetPrice(Math.round(currentPrice * (1 - percent / 100)))}
                  className="flex-1 py-2 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  -{percent}%
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !email}
          className="w-full py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Creating Alert...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-notification-3-line"></i>
              Create Price Alert
            </span>
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          We will only email you about price changes for this product.
        </p>
      </form>
    </div>
  );
};

export default PriceAlertForm;
